import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Shield,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  Plus,
} from 'lucide-react';
import { ZKPSetupFlow } from './ZKPSetupFlow';
import { completeEarningAction } from '@/api/rewards';

export interface ZKPProof {
  id: string;
  type:
    | 'vaccination'
    | 'age-eligibility'
    | 'weight-eligibility'
    | 'allergy-presence'
    | 'medication-status'
    | 'license-valid';
  status: 'active' | 'expiring-soon' | 'expired';
  created: string;
  expires: string;
  context: string;
  verified: boolean;
}

interface ZKPVerificationManagerProps {
  petId?: string;
  professionalId?: string;
  onClose: () => void;
}

function generateProofId(): string {
  return `zkp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function computeStatus(
  expires: string
): 'active' | 'expiring-soon' | 'expired' {
  const expiry = new Date(expires);
  const today = new Date();
  const daysUntil = Math.floor(
    (expiry.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
  );
  if (daysUntil < 0) return 'expired';
  if (daysUntil <= 30) return 'expiring-soon';
  return 'active';
}

export function ZKPVerificationManager({
  petId: _petId,
  professionalId: _professionalId,
  onClose,
}: ZKPVerificationManagerProps) {
  const { t, i18n } = useTranslation();
  const [activeProofs, setActiveProofs] = useState<ZKPProof[]>([]);
  const [selectedProof, setSelectedProof] = useState<ZKPProof | null>(null);
  const [showCreateFlow, setShowCreateFlow] = useState(false);

  const getProofLabel = (type: ZKPProof['type']): string => {
    return t(`landing.features.zkp.proofTypes.${type}`, type);
  };

  const getStatusColor = (status: ZKPProof['status']) => {
    switch (status) {
      case 'active':
        return {
          bg: '#10B981',
          text: '#FFFFFF',
          badge: '#DCFCE7',
          badgeText: '#059669',
        };
      case 'expiring-soon':
        return {
          bg: '#F59E0B',
          text: '#FFFFFF',
          badge: '#FEF3C7',
          badgeText: '#D97706',
        };
      case 'expired':
        return {
          bg: '#EF4444',
          text: '#FFFFFF',
          badge: '#FEE2E2',
          badgeText: '#DC2626',
        };
    }
  };

  const getStatusIcon = (status: ZKPProof['status']) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5" />;
      case 'expiring-soon':
        return <Clock className="w-5 h-5" />;
      case 'expired':
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getDaysUntilExpiry = (expiryDate: string): number => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    return Math.floor(
      (expiry.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
    );
  };

  const handleRefresh = (proofId: string) => {
    // Simulate refresh
    setActiveProofs((proofs) =>
      proofs.map((p) =>
        p.id === proofId
          ? {
              ...p,
              status: 'active',
              created: new Date().toISOString().split('T')[0],
              expires: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split('T')[0],
              verified: true,
            }
          : p
      )
    );
  };

  const handleRevoke = (proofId: string) => {
    setActiveProofs((proofs) => proofs.filter((p) => p.id !== proofId));
  };

  const handleCreateComplete = (proofData: {
    type?: ZKPProof['type'];
    verified?: boolean;
    created?: string;
    expires?: string;
    context?: string;
  }) => {
    const id = generateProofId();
    const expiresStr =
      proofData.expires ??
      new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString();
    const expires = expiresStr.split('T')[0];
    const status = computeStatus(expires);
    const createdStr = proofData.created ?? new Date().toISOString();
    const created = createdStr.split('T')[0];
    const newProof: ZKPProof = {
      id,
      type: proofData.type ?? 'vaccination',
      status,
      created,
      expires,
      context: proofData.context ?? 'sharing',
      verified: proofData.verified ?? true,
    };
    setActiveProofs((prev) => [newProof, ...prev]);
    setShowCreateFlow(false);

    // Award ZKP Usage reward (409 means already awarded — show nothing)
    completeEarningAction('ZKP1')
      .then((result) => {
        toast.success(`+${result.pointsAwarded} PETS earned!`, {
          description: result.message || 'ZKP Verification reward',
          duration: 5000,
        });
      })
      .catch(() => {});
  };

  // Create Verification Flow
  if (showCreateFlow) {
    return (
      <div className="bg-white dark:bg-[#16151D] rounded-2xl p-6 max-w-4xl mx-auto border dark:border-white/8">
        <ZKPSetupFlow
          verificationType="vaccination"
          context="sharing"
          onComplete={handleCreateComplete}
          onCancel={() => setShowCreateFlow(false)}
        />
      </div>
    );
  }

  // If viewing details of a specific proof
  if (selectedProof) {
    const colors = getStatusColor(selectedProof.status);
    const daysLeft = getDaysUntilExpiry(selectedProof.expires);

    return (
      <div className="bg-white dark:bg-[#16151D] rounded-2xl p-6 max-w-4xl mx-auto border dark:border-white/8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: colors.badge, color: colors.badgeText }}
            >
              {getStatusIcon(selectedProof.status)}
            </div>
            <div>
              <h2
                className="text-2xl dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {getProofLabel(selectedProof.type)}
              </h2>
              <p
                className="text-sm text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'landing.features.zkp.verificationDetails',
                  'Verification Details'
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => setSelectedProof(null)}
            className="text-gray-500 dark:text-white/50 hover:text-gray-700 dark:hover:text-white/70 transition-colors"
          >
            <span className="text-2xl">×</span>
          </button>
        </div>

        {/* Status Badge */}
        <div className="mb-6">
          <span
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ backgroundColor: colors.badge, color: colors.badgeText }}
          >
            {selectedProof.status === 'active' &&
              t(
                'landing.features.zkp.activeVerification',
                '✓ Active Verification'
              )}
            {selectedProof.status === 'expiring-soon' &&
              t('landing.features.zkp.expiringSoon', '⚠️ Expiring Soon')}
            {selectedProof.status === 'expired' &&
              t('landing.features.zkp.expired', 'Expired')}
          </span>
        </div>

        {/* Details Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-xl p-5 border dark:border-white/8">
            <h4
              className="text-sm text-gray-600 dark:text-white/50 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('landing.features.zkp.verificationId', 'Verification ID')}
            </h4>
            <p
              className="text-gray-900 dark:text-white font-mono text-sm"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {selectedProof.id}
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-xl p-5 border dark:border-white/8">
            <h4
              className="text-sm text-gray-600 dark:text-white/50 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('landing.features.zkp.verificationType', 'Verification Type')}
            </h4>
            <p
              className="text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {getProofLabel(selectedProof.type)}
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-xl p-5 border dark:border-white/8">
            <h4
              className="text-sm text-gray-600 dark:text-white/50 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('landing.features.zkp.createdDate', 'Created Date')}
            </h4>
            <p
              className="text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {new Date(selectedProof.created).toLocaleDateString(
                i18n.language || 'it',
                {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }
              )}
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-xl p-5 border dark:border-white/8">
            <h4
              className="text-sm text-gray-600 dark:text-white/50 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('landing.features.zkp.expirationDate', 'Expiration Date')}
            </h4>
            <p
              className="text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {new Date(selectedProof.expires).toLocaleDateString(
                i18n.language || 'it',
                {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }
              )}
              {selectedProof.status !== 'expired' && (
                <span className="text-sm text-gray-600 dark:text-white/50 ml-2">
                  (
                  {t('landing.features.zkp.daysRemaining', { count: daysLeft })}
                  )
                </span>
              )}
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-xl p-5 border dark:border-white/8">
            <h4
              className="text-sm text-gray-600 dark:text-white/50 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('landing.features.zkp.usedFor', 'Used For')}
            </h4>
            <p
              className="text-gray-900 dark:text-white capitalize"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {selectedProof.context.replace('-', ' ')}
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-xl p-5 border dark:border-white/8">
            <h4
              className="text-sm text-gray-600 dark:text-white/50 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t(
                'landing.features.zkp.verificationStatus',
                'Verification Status'
              )}
            </h4>
            <p
              className="text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {selectedProof.verified
                ? t('landing.features.zkp.verified', '✓ Verified')
                : t('landing.features.zkp.notVerified', '✗ Not Verified')}
            </p>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-xl p-5 mb-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4
                className="font-semibold mb-2 text-blue-900 dark:text-blue-100"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {t(
                  'landing.features.zkp.privacyProtected',
                  'Privacy Protected'
                )}
              </h4>
              <p
                className="text-sm text-gray-700 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'landing.features.zkp.privacyProtectedDesc',
                  "This verification uses zero-knowledge proofs to confirm conditions without revealing sensitive data. Professionals see only a verified checkmark, not your pet's private health records."
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Timeline Progress */}
        {selectedProof.status !== 'expired' && (
          <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-xl p-5 border dark:border-white/8 mb-6">
            <h4
              className="font-semibold mb-4 text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {t(
                'landing.features.zkp.verificationTimeline',
                'Verification Timeline'
              )}
            </h4>
            <div className="relative">
              <div className="h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden mb-2">
                <div
                  className="h-full transition-all duration-300"
                  style={{
                    width: `${Math.max(0, Math.min(100, (daysLeft / 180) * 100))}%`,
                    backgroundColor: colors.bg,
                  }}
                />
              </div>
              <div
                className="flex justify-between text-xs text-gray-600 dark:text-white/50"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <span>{t('landing.features.zkp.created', 'Created')}</span>
                <span>
                  {t('landing.features.zkp.daysRemaining', { count: daysLeft })}
                </span>
                <span>{t('landing.features.zkp.expires', 'Expires')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => setSelectedProof(null)}
            className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 dark:border-white/20 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-700 dark:text-white/90"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('landing.features.zkp.backToList', 'Back to List')}
          </button>
          {(selectedProof.status === 'expired' ||
            selectedProof.status === 'expiring-soon') && (
            <button
              onClick={() => {
                handleRefresh(selectedProof.id);
                setSelectedProof(null);
              }}
              className="flex-1 px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
              }}
            >
              <RefreshCw className="w-5 h-5" />
              {t('landing.features.zkp.reverifyNow', 'Re-verify Now')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#16151D] rounded-2xl p-4 sm:p-6 max-w-4xl mx-auto border dark:border-white/8 min-w-0">
      {/* Header: stack on mobile so "Create Verification" button is never cut off */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <div className="min-w-0">
            <h2
              className="text-xl sm:text-2xl dark:text-white truncate"
              style={{
                color: '#2D2F7E',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
              }}
            >
              {t('landing.features.zkp.title', 'Privacy Verifications')}
            </h2>
            <p
              className="text-sm text-gray-600 dark:text-white/70"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t(
                'landing.features.zkp.subtitle',
                'Create and manage privacy-protected verifications'
              )}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateFlow(true)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white hover:opacity-90 transition-opacity flex-shrink-0 w-full sm:w-auto cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
            fontFamily: 'Sora, sans-serif',
            fontWeight: 700,
          }}
        >
          <Plus className="w-5 h-5" />
          {t('landing.features.zkp.createVerification', 'Create Verification')}
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-xl p-5 mb-6">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3
              className="font-semibold mb-2 text-blue-900 dark:text-blue-100"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {t(
                'landing.features.zkp.verifyWithoutSharing',
                'Verify Without Sharing'
              )}
            </h3>
            <p
              className="text-sm mb-2 text-gray-700 dark:text-white/70"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t(
                'landing.features.zkp.verifyWithoutSharingDesc',
                'Petso can confirm required information without exposing private details. Verifications prove conditions are met without revealing sensitive data.'
              )}
            </p>
            <ul
              className="text-sm space-y-1 text-gray-700 dark:text-white/70"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <li>
                •{' '}
                {t(
                  'landing.features.zkp.professionalsSeeVerified',
                  'Professionals see ✅ "Verified" not your private records'
                )}
              </li>
              <li>
                •{' '}
                {t(
                  'landing.features.zkp.proofsExpire',
                  'Proofs expire automatically for security'
                )}
              </li>
              <li>
                •{' '}
                {t(
                  'landing.features.zkp.youControl',
                  'You control what gets verified'
                )}
              </li>
              <li>
                •{' '}
                {t(
                  'landing.features.zkp.fullDataOption',
                  'Full data sharing is always an option'
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Active Verifications */}
      <div className="mb-6">
        <h3
          className="text-xl mb-4 dark:text-white"
          style={{
            color: '#2D2F7E',
            fontFamily: 'Sora, sans-serif',
            fontWeight: 700,
          }}
        >
          {t(
            'landing.features.zkp.activeVerifications',
            'Active Verifications'
          )}
        </h3>

        {activeProofs.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 dark:bg-[#0F0E16] rounded-xl border dark:border-white/8">
            <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-white/20" />
            <p
              className="text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t(
                'landing.features.zkp.noVerificationsYet',
                'No verifications yet. Create one to get started.'
              )}
            </p>
            <p
              className="text-sm text-gray-500 dark:text-white/50 mb-6"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t(
                'landing.features.zkp.noVerificationsDesc',
                'Privacy verifications prove conditions without exposing sensitive data.'
              )}
            </p>
            <button
              onClick={() => setShowCreateFlow(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
              }}
            >
              <Plus className="w-5 h-5" />
              {t(
                'landing.features.zkp.createFirstVerification',
                'Create Your First Verification'
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {activeProofs.map((proof) => {
              const colors = getStatusColor(proof.status);
              const daysLeft = getDaysUntilExpiry(proof.expires);

              return (
                <div
                  key={proof.id}
                  className="rounded-xl p-5 bg-white dark:bg-[#0F0E16] border-2 border-gray-200 dark:border-white/8"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{
                          backgroundColor: colors.badge,
                          color: colors.badgeText,
                        }}
                      >
                        {getStatusIcon(proof.status)}
                      </div>
                      <div className="flex-1">
                        <h4
                          className="font-semibold mb-1 text-gray-900 dark:text-white"
                          style={{ fontFamily: 'Sora, sans-serif' }}
                        >
                          {getProofLabel(proof.type)}
                        </h4>
                        <div className="flex items-center gap-2 mb-2">
                          <span
                            className="px-2 py-1 rounded-md text-xs font-semibold"
                            style={{
                              backgroundColor: colors.badge,
                              color: colors.badgeText,
                            }}
                          >
                            {proof.status === 'active' &&
                              t('petOverview.didActive', 'Active')}
                            {proof.status === 'expiring-soon' &&
                              t(
                                'landing.features.zkp.expiringSoon',
                                'Expiring Soon'
                              )}
                            {proof.status === 'expired' &&
                              t('landing.features.zkp.expired', 'Expired')}
                          </span>
                          <span
                            className="text-xs text-gray-600 dark:text-white/50"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {t(
                              'landing.features.zkp.usedForLabel',
                              'Used for:'
                            )}{' '}
                            {proof.context.replace('-', ' ')}
                          </span>
                        </div>
                        <div
                          className="flex items-center gap-4 text-xs text-gray-600 dark:text-white/50"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          <span>
                            {t('landing.features.zkp.created', 'Created')}:{' '}
                            {new Date(proof.created).toLocaleDateString()}
                          </span>
                          <span>
                            {t('landing.features.zkp.expires', 'Expires')}:{' '}
                            {new Date(proof.expires).toLocaleDateString()}
                            {proof.status !== 'expired' &&
                              ` (${t('landing.features.zkp.daysRemaining', { count: daysLeft })})`}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {proof.verified && (
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{
                            backgroundColor: colors.badge,
                            color: colors.badgeText,
                          }}
                        >
                          ✓
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {proof.status !== 'expired' && (
                    <div className="mb-4">
                      <div className="h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full transition-all duration-300"
                          style={{
                            width: `${Math.max(0, Math.min(100, (daysLeft / 180) * 100))}%`,
                            backgroundColor: colors.bg,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    {proof.status === 'expired' ||
                    proof.status === 'expiring-soon' ? (
                      <button
                        onClick={() => handleRefresh(proof.id)}
                        className="flex-1 px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-sm"
                        style={{
                          background:
                            'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 700,
                        }}
                      >
                        <RefreshCw className="w-4 h-4" />
                        {t('landing.features.zkp.reverifyNow', 'Re-verify Now')}
                      </button>
                    ) : (
                      <button
                        onClick={() => setSelectedProof(proof)}
                        className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-white/20 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-sm text-gray-700 dark:text-white/90"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 700,
                        }}
                      >
                        {t('landing.features.zkp.viewDetails', 'View Details')}
                      </button>
                    )}
                    <button
                      onClick={() => handleRevoke(proof.id)}
                      className="px-4 py-2 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-red-600 dark:text-red-400 text-sm"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {t('landing.features.zkp.revoke', 'Revoke')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Data State Reference */}
      <div className="bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-xl p-5">
        <h3
          className="font-semibold mb-3 text-gray-900 dark:text-white"
          style={{ fontFamily: 'Sora, sans-serif' }}
        >
          {t(
            'landing.features.zkp.understandingVerificationStates',
            'Understanding Verification States'
          )}
        </h3>
        <div
          className="space-y-2 text-sm"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              ✅
            </div>
            <div className="flex-1">
              <strong className="text-gray-900 dark:text-white">
                {t('landing.features.zkp.verifiedLabel', 'Verified')}
              </strong>
              <span className="text-gray-600 dark:text-white/70">
                {' '}
                -{' '}
                {t(
                  'landing.features.zkp.verifiedDesc',
                  'Condition met (privacy-protected)'
                )}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              🔓
            </div>
            <div className="flex-1">
              <strong className="text-gray-900 dark:text-white">
                {t('landing.features.zkp.fullAccessLabel', 'Full Access')}
              </strong>
              <span className="text-gray-600 dark:text-white/70">
                {' '}
                -{' '}
                {t(
                  'landing.features.zkp.fullAccessDesc',
                  'Data shared with consent'
                )}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-200 dark:bg-white/10 rounded-lg flex items-center justify-center">
              🔒
            </div>
            <div className="flex-1">
              <strong className="text-gray-900 dark:text-white">
                {t('landing.features.zkp.hiddenLabel', 'Hidden')}
              </strong>
              <span className="text-gray-600 dark:text-white/70">
                {' '}
                - {t('landing.features.zkp.hiddenDesc', 'No access granted')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Close Button */}
      <div className="mt-6">
        <button
          onClick={onClose}
          className="w-full px-6 py-3 rounded-xl border-2 border-gray-300 dark:border-white/20 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-700 dark:text-white/90 cursor-pointer"
          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
        >
          {t('common.close', 'Close')}
        </button>
      </div>
    </div>
  );
}
